/**
 * Domain-agnostic SDK wrapper for the knowledge-bases list endpoint.
 *
 * Lifted from `features/knowledge-bases/api/knowledgeBases.api.ts` so any
 * surface that needs the KB list (the knowledge-bases feature itself,
 * `brief-generation` intake, etc.) can consume it without crossing into
 * the `knowledge-bases` feature slice.
 *
 * The original feature path re-exports this symbol for backward
 * compatibility — existing consumers keep working unchanged.
 */
import { assetsHttp } from '@/lib/api/assets-client';
import {
  KnowledgeBasesService,
  EnhancedKnowledgeBaseSummaryDto,
  EnhancedRagFileMetadataDto,
} from '@inference.engine/assets-api-client/web';

export const getKnowledgeBases = async (
  workspaceId?: string,
  includeInternal?: boolean,
): Promise<EnhancedKnowledgeBaseSummaryDto[]> => {
  try {
    const response = await KnowledgeBasesService.listKnowledgeBases({
      client: assetsHttp,
      query: {
        workspaceId,
        includeInternal,
      },
    });

    if (!response.data) {
      throw new Error('Empty response from getKnowledgeBases');
    }

    return response.data;
  } catch (error) {
    throw new Error(
      `Failed to get knowledge bases: ${error instanceof Error ? error.message : 'Unknown error'}`,
      { cause: error },
    );
  }
};

/**
 * List files across multiple knowledge bases filtered by file extension (e.g. ".campaign").
 * Domain-agnostic so non-feature surfaces (e.g. the Templates chat options) can consume it without
 * crossing into the `knowledge-bases` feature slice.
 */
export const getFilesByExtension = async (
  knowledgeBaseIds: string[],
  extension: string,
): Promise<EnhancedRagFileMetadataDto[]> => {
  if (knowledgeBaseIds.length === 0) return [];
  try {
    const response = await KnowledgeBasesService.getFilesByExtension({
      client: assetsHttp,
      query: { knowledgeBaseIds, extension },
    });

    if (!response.data) {
      throw new Error('Empty response from getFilesByExtension');
    }

    return response.data;
  } catch (error) {
    throw new Error(
      `Failed to list files by extension: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`,
      { cause: error },
    );
  }
};
